export type DockAxis = "width" | "height" | "sideHeight";

export interface DockLimits {
  min: number;
  max: number;
}

interface AxisConfig {
  storageKey: string;
  label: string;
  orientation: "horizontal" | "vertical";
  growKey: string;
  shrinkKey: string;
  limits: (viewport: number) => DockLimits;
  defaultSize: (viewport: number) => number;
}

export const DOCK_SHEET_BREAKPOINT = 1024;
export const FULL_DOCK_HEIGHT = Number.MAX_SAFE_INTEGER;

const STEP = 24;
const LARGE_STEP = 96;

export const AXIS_CONFIG: Record<DockAxis, AxisConfig> = {
  width: {
    storageKey: "form-assistant-dock-width",
    label: "Resize assistant width",
    orientation: "vertical",
    growKey: "ArrowLeft",
    shrinkKey: "ArrowRight",
    limits: (viewport) => ({
      min: 320,
      max: Math.max(320, Math.min(720, Math.round(viewport * 0.6))),
    }),
    defaultSize: () => 400,
  },
  height: {
    storageKey: "form-assistant-dock-height",
    label: "Resize assistant height",
    orientation: "horizontal",
    growKey: "ArrowUp",
    shrinkKey: "ArrowDown",
    limits: (viewport) => ({ min: 200, max: Math.max(200, Math.round(viewport * 0.9)) }),
    defaultSize: (viewport) => Math.min(480, Math.round(viewport * 0.6)),
  },
  sideHeight: {
    storageKey: "form-assistant-dock-side-height",
    label: "Resize assistant height",
    orientation: "horizontal",
    growKey: "ArrowUp",
    shrinkKey: "ArrowDown",
    limits: (viewport) => {
      const max = Math.max(0, Math.round(viewport));
      return { min: Math.min(360, max), max };
    },
    defaultSize: () => FULL_DOCK_HEIGHT,
  },
};

export function dockLimits(axis: DockAxis, viewport: number): DockLimits {
  return AXIS_CONFIG[axis].limits(viewport);
}

export function defaultDockSize(axis: DockAxis, viewport: number): number {
  return AXIS_CONFIG[axis].defaultSize(viewport);
}

export function clampDockSize(value: number, { min, max }: DockLimits): number {
  return Math.round(Math.min(max, Math.max(min, value)));
}

export function readDockSize(axis: DockAxis): number | null {
  try {
    const stored = Number(sessionStorage.getItem(AXIS_CONFIG[axis].storageKey));
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  } catch {
    return null;
  }
}

export function writeDockSize(axis: DockAxis, size: number) {
  try {
    sessionStorage.setItem(AXIS_CONFIG[axis].storageKey, String(size));
  } catch {
    // Not kept for a reload.
  }
}

export function nextDockSizeForKey(
  key: string,
  axis: DockAxis,
  size: number,
  limits: DockLimits,
  largeStep: boolean,
): number | null {
  const { growKey, shrinkKey } = AXIS_CONFIG[axis];
  const step = largeStep ? LARGE_STEP : STEP;
  if (key === "Home") return limits.min;
  if (key === "End") return limits.max;
  if (key === growKey) return clampDockSize(size + step, limits);
  if (key === shrinkKey) return clampDockSize(size - step, limits);
  return null;
}
